import { useEffect, useState } from "react";

import JsBarcode from "jsbarcode";
import bwipjs from "bwip-js";

import pako from "pako";
import { formatBarcodeValue } from "../components/Barcode/helper.js";
import BrowserPrint from "./printer.js";

const defaultOptions = {
  height: 100,
  width: 200,
  displayValue: true,
};

///////////////////////////////////
///////////////////////////////////
//// BMP HELPER FUNCTION START ////
///////////////////////////////////
///////////////////////////////////

function crc16(str) {
  let crc = 0x0000;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
    }
  }
  return crc & 0xffff;
}

export const getScaleY = (n) => {
  if (n <= 1) return 1;
  if (n < 1.3) return 1.25;
  if (n < 1.6) return 1.5;
  if (n < 1.85) return 1.75;
  return 2;
};

export const renderBarcodeToCanvas = (
  canvas,
  value,
  format,
  options = {},
  scaleBar = 1,
) => {
  const { width, height = 80, ...rest } = options;
  if (format === "PDF417") {
    bwipjs.toCanvas(canvas, {
      bcid: "pdf417",
      text: value,
      scale: 5,
      height: 15,
      backgroundcolor: "#FFF",
    });
  } else {
    const scale = Math.round((100 / height) * 100) / 100;
    const formatted = formatBarcodeValue(value, format);

    const chars = formatted.length;
    const containerWidth = width;
    const scaley = getScaleY(chars / 11);
    const barWidth = (containerWidth * scaley) / (chars * 11);
    JsBarcode(canvas, formatted, {
      format,
      displayValue: true,
      fontSize: 18,
      width: barWidth * scaleBar,
      ...rest,
    });
  }
};

const scaleCanvase = (canvas, scale = 1, width, height) => {
  const scaledCanvas = document.createElement("canvas");
  scaledCanvas.width = width || canvas.width;
  scaledCanvas.height = (height || canvas.height) * scale;

  const sctx = scaledCanvas.getContext("2d");

  // IMPORTANT: keep bars crisp
  sctx.imageSmoothingEnabled = false;

  sctx.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    canvas.height,
    0,
    0,
    scaledCanvas.width,
    scaledCanvas.height,
  );

  return scaledCanvas;
};
export function barcodeToZ64(value, format, options = defaultOptions) {
  const scale = options.scale ?? 1.20;

  // Base canvas (barcode library renders here)
  const canvas = document.createElement("canvas");
  const styleWidth = Math.ceil(options.width / 8) * 8;

  canvas.width = styleWidth;
  canvas.height = options.height;

  renderBarcodeToCanvas(canvas, value, format, options, scale);

  let srcCanvas = canvas;

  // SCALE STEP
  if (scale !== 1 && format !== "PDF417") {
    srcCanvas = scaleCanvase(canvas, scale);
  } else if (format === "PDF417") {
    srcCanvas = scaleCanvase(canvas, 2, styleWidth * 2, options.height);
  }

  const ctx = srcCanvas.getContext("2d");
  const { width, height } = srcCanvas;

  const imageData = ctx.getImageData(0, 0, width, height).data;

  const bpr = Math.ceil(width / 8);
  const bitmap = new Uint8Array(bpr * height);

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const i = (row * width + col) * 4;

      const r = imageData[i];
      const g = imageData[i + 1];
      const b = imageData[i + 2];

      const grayscale = 0.299 * r + 0.587 * g + 0.114 * b;

      if (grayscale < 128) {
        const byteIndex = row * bpr + (col >> 3);
        const bitIndex = 7 - (col & 7);

        bitmap[byteIndex] |= 1 << bitIndex;
      }
    }
  }

  const compressed = pako.deflate(bitmap);

  const base64 = btoa(
    Array.from(compressed, (b) => String.fromCharCode(b)).join(""),
  );

  const crc = crc16(base64);
  const crcHex = crc.toString(16).toUpperCase().padStart(4, "0");

  return {
    z64: `:Z64:${base64}:${crcHex}`,
    totalBytes: bitmap.length,
    compressed: compressed.length,
    bpr,
    width,
    height,
    scale,
  };
}
///////////////////////////////////
///////////////////////////////////
///// BMP HELPER FUNCTION END /////
///////////////////////////////////
///////////////////////////////////

///////////////////////////////////
///////////////////////////////////
//// ZPL HELPER FUNCTION START ////
///////////////////////////////////
///////////////////////////////////

// ZPL fonts:
// ^A0N,h,w where h/w are dot heights/widths.
const fontSpec = (h) => `^A0N,${h},${Math.round(h)}`;

const esc = (s) =>
  String(s ?? "")
    .replace(/[\^~]/g, " ")
    .replace(/[\r\n]+/g, " ")
    .trim();

const hasAnyTextRow = (row) =>
  Array.isArray(row) && row.some((x) => esc(x).length > 0);

const getTextWidth = (text, FONT_SIZE = 25) =>
  Math.round((esc(text).length * FONT_SIZE) / 2);

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const getRightClamp = (text, labelW, PADDING, FONT_SIZE = 25) => {
  const textWidth = getTextWidth(text, FONT_SIZE);
  const floatR = Math.round(labelW - textWidth);
  const righted = clamp(floatR, PADDING, labelW - PADDING);
  return righted;
};

const getCenterClamp = (text, labelW, PADDING, FONT_SIZE = 25) => {
  const textWidth = getTextWidth(text, FONT_SIZE);
  const center = (labelW - textWidth) / 2;
  const accPadding = center + PADDING / 2;
  const rCenter = Math.round(accPadding);
  const centered = clamp(rCenter, PADDING, labelW - PADDING);
  return centered;
};

const getClampFns = ({ labelW, FONT_SIZE, PADDING }) => {
  return {
    getCenClamp: (text) => getCenterClamp(text, labelW, PADDING, FONT_SIZE),
    getRClamp: (text) => getRightClamp(text, labelW, PADDING, FONT_SIZE),
  };
};

///////////////////////////////////
///////////////////////////////////
///// ZPL HELPER FUNCTION END /////
///////////////////////////////////
///////////////////////////////////

export const generateBmpZpl = ({ size = {}, barcodes = [] }) => {
  if (!Array.isArray(barcodes)) throw new Error("barcodes must be an array");

  // ---- Size / unit handling ----
  const dpi = size.dpi ?? 203;
  const unit = size.unit ?? "in"; // "in" | "mm" | "dots"

  const toDots = (v) => {
    if (unit === "dots") return Math.round(v);
    if (unit === "mm") return Math.round((v / 25.4) * dpi);
    // inches
    return Math.round(v * dpi);
  };

  const labelW = toDots(size.width ?? 2.625);
  const labelH = toDots(size.height ?? 1.0);

  // ---- "px" to dots conversion (CSS px baseline @96dpi) ----
  const dotsPerPx = dpi / 96;
  const pxToDots = (px) => Math.round(px * dotsPerPx);

  const PADDING = pxToDots(12);
  const GAP = pxToDots(2);

  // Fonts (requested): top/bottom 12px, barcode label 14px
  const FONT_SIZE = pxToDots(12);

  const out = [];

  for (const item of barcodes) {
    const top = Array.isArray(item?.top) ? item.top.map(esc) : ["", "", ""];
    const bottom = Array.isArray(item?.bottom)
      ? item.bottom.map(esc)
      : ["", "", ""];

    const hasTop = hasAnyTextRow(top);
    const hasBottom = hasAnyTextRow(bottom);

    // Vertical layout
    const yTop = PADDING;
    const yBarcodeTop = yTop + (hasTop ? FONT_SIZE + GAP : 0);

    // Bottom row sits at the bottom of padded content area
    const yBottom = labelH - PADDING - FONT_SIZE;

    // Generate Helper Functions
    const clampProps = { labelW, PADDING, FONT_SIZE };
    const { getRClamp, getCenClamp } = getClampFns(clampProps);

    // X layout (left/center/right)
    const xLeft = PADDING;
    const xRightTop = getRClamp(top[2]);
    const xRightBottom = getRClamp(bottom[2]);
    const xCenterTop = getCenClamp(top[1]);
    const xCenterBottom = getCenClamp(bottom[1]);

    out.push("^XA");
    out.push(`^PW${labelW}`);
    out.push(`^LL${labelH}`);
    out.push("^CI28");

    // TOP
    const topSpec = `,${yTop}${fontSpec(FONT_SIZE)}^FD`;
    if (hasTop) {
      if (top[0]) out.push(`^FO${xLeft}${topSpec}${top[0]}^FS`);
      if (top[1]) out.push(`^FO${xCenterTop}${topSpec}${top[1]}^FS`);
      if (top[2]) out.push(`^FO${xRightTop}${topSpec}${top[2]}^FS`);
    }

    // BARCODE (bitmap)
    const { z64, totalBytes, compressed, bpr, width } = item.barcode;

    // Center the bitmap horizontally
    const xBarcode = Math.floor((labelW - width) / 2);
    const xBarcodeF = `${xBarcode},${yBarcodeTop}`;
    const xBarcodeGFA = `${compressed},${totalBytes},${bpr},${z64}`;
    out.push(`^FO${xBarcodeF}^GFA,${xBarcodeGFA}^FS`);

    // BOTTOM
    const botSpec = `,${yBottom}${fontSpec(FONT_SIZE)}^FD`;
    if (hasBottom) {
      if (bottom[0]) out.push(`^FO${xLeft}${botSpec}${bottom[0]}^FS`);
      if (bottom[1]) out.push(`^FO${xCenterBottom}${botSpec}${bottom[1]}^FS`);
      if (bottom[2]) out.push(`^FO${xRightBottom}${botSpec}${bottom[2]}^FS`);
    }

    out.push("^XZ");
  }

  return out.join("\n");
};

export function generateZpl({
  format = "CODE39",
  showLabel = true,
  size = {},
  barcodes = [],
} = {}) {
  if (!Array.isArray(barcodes)) throw new Error("barcodes must be an array");

  // ---- Size / unit handling ----
  const dpi = size.dpi ?? 203;
  const unit = size.unit ?? "in"; // "in" | "mm" | "dots"

  const toDots = (v) => {
    if (unit === "dots") return Math.round(v);
    if (unit === "mm") return Math.round((v / 25.4) * dpi);
    // inches
    return Math.round(v * dpi);
  };

  const labelW = toDots(size.width ?? 2.625);
  const labelH = toDots(size.height ?? 1.0);

  // ---- "px" to dots conversion (CSS px baseline @96dpi) ----
  const dotsPerPx = dpi / 96;
  const pxToDots = (px) => Math.round(px * dotsPerPx);

  const PADDING = pxToDots(12);
  const GAP = pxToDots(2);

  // Fonts (requested): top/bottom 12px, barcode label 14px
  const FONT_SIZE = pxToDots(12);
  const FONT_BARCODE_LABEL = pxToDots(14);

  // ---- Barcode ZPL mapping ----
  // We map your format list to ZPL commands.
  // For the ones ZPL doesn't natively support as a single command (like MSI variants),
  // you typically handle check digits yourself and still use ^BC or other symbologies.
  // Here we provide reasonable defaults and note limitations.
  function barcodeCommandFor(formatId) {
    switch (formatId) {
      case "CODE39":
        // ^B3 = Code 39
        return { cmd: "^B3", supports: true };
      case "CODE128":
      case "CODE128A":
      case "CODE128B":
      case "CODE128C":
        // ^BC = Code 128 (printer will encode; A/B/C subsets can be driven by data/control chars)
        return { cmd: "^BC", supports: true };
      case "EAN13":
        // ^BE = EAN-13
        return { cmd: "^BE", supports: true };
      case "EAN8":
        // ^B8 = EAN-8
        return { cmd: "^B8", supports: true };
      case "UPC":
        // ^BU = UPC-A (common)
        return { cmd: "^BU", supports: true };
      case "ITF14":
        // ^B2 = Interleaved 2 of 5 (often used for ITF / ITF-14 with proper data)
        return { cmd: "^B2", supports: true };
      case "codabar":
        // ^BK = Codabar
        return { cmd: "^BK", supports: true };
      case "pharmacode":
      case "MSI":
      case "MSI10":
      case "MSI11":
      case "MSI1010":
      case "MSI1110":
        // Zebra ZPL support can vary by model/firmware for MSI/Pharmacode.
        // If your printer supports it, it may be ^BM (MSI) and ^BP (Pharmacode) on some firmwares,
        // but not universally. Safer: treat as unsupported until you confirm.
        return { cmd: null, supports: false };
      default:
        return { cmd: "^BC", supports: true };
    }
  }

  const { cmd: bcCmd, supports } = barcodeCommandFor(format);

  if (!supports) {
    throw new Error(
      `Format "${format}" is not mapped safely to a standard ZPL barcode command for all Zebra models. ` +
        `If you confirm your printer supports it, we can add the correct ZPL command.`,
    );
  }

  // ---- Barcode sizing logic ----
  // Height "fills remaining space", with special rule:
  // If there is no top and/or bottom, barcode height is capped at 75% of label height.
  //
  // We treat "top block" and "bottom block" as:
  //  - if any top text exists: FONT_SIZE + GAP
  //  - if any bottom text exists: FONT_SIZE + GAP
  // Barcode label (human readable) occupies FONT_BARCODE_LABEL + GAP when showLabel=true.
  //
  // Then barcode height = remaining within padded content area.
  function computeBarcodeHeight({ hasTop, hasBottom }) {
    const contentH = labelH - 2 * PADDING;
    if (contentH <= 0) return pxToDots(20);

    const topBlock = hasTop ? FONT_SIZE + GAP : 0;
    const bottomBlock = hasBottom ? FONT_SIZE + GAP : 0;
    const labelBlock = showLabel ? FONT_BARCODE_LABEL + GAP : 0;

    // Remaining space for bars:
    let barsH = contentH - topBlock - bottomBlock - labelBlock;

    // Prevent tiny/negative heights
    barsH = Math.max(pxToDots(14), barsH);

    // If missing top OR bottom (i.e., not both present), cap to 75% of label height
    if (!(hasTop && hasBottom)) {
      const cap = Math.round(labelH * 0.75);
      barsH = Math.min(barsH, cap);
    }

    return barsH;
  }

  // ---- Module width (^BY) ----
  // We keep it consistent and relatively small so long strings fit better.
  // You can tune: 1 is narrow, 2-3 common.
  const MODULE_WIDTH = 2; // dots
  const WIDE_NARROW_RATIO = 2;

  const out = [];

  for (const item of barcodes) {
    const code = esc(item?.barcode);
    if (!code) continue;

    const top = Array.isArray(item?.top) ? item.top.map(esc) : ["", "", ""];
    const bottom = Array.isArray(item?.bottom)
      ? item.bottom.map(esc)
      : ["", "", ""];

    const hasTop = hasAnyTextRow(top);
    const hasBottom = hasAnyTextRow(bottom);

    // Vertical layout
    const yTop = PADDING;
    const yBarcodeTop = yTop + (hasTop ? FONT_SIZE + GAP : 0);

    const barsH = computeBarcodeHeight({ hasTop, hasBottom });
    const yLabel = yBarcodeTop + barsH + GAP;

    // Bottom row sits at the bottom of padded content area
    const yBottom = labelH - PADDING - FONT_SIZE;

    // Generate Helper Functions
    const clampProps = { labelW, PADDING, FONT_SIZE };
    const { getRClamp, getCenClamp } = getClampFns(clampProps);

    // X layout (left/center/right)
    const xLeft = PADDING;
    const xRightTop = getRClamp(top[2]);
    const xRightBottom = getRClamp(bottom[2]);
    const xCenterTop = getCenClamp(top[1]);
    const xCenterBottom = getCenClamp(bottom[1]);

    // Barcode X: just start at left padding (doesn't need to fill width)
    // Rough estimate of barcode width
    const estimateBarcodeWidth = (value) => {
      const chars = value.length;

      // CODE128 ~ 11 modules per char + start/stop
      const modulesPerChar = 11;
      const overheadModules = 35;

      const totalModules = chars * modulesPerChar + overheadModules;

      return totalModules * MODULE_WIDTH;
    };

    const barcodeWidth = estimateBarcodeWidth(code);

    // Center the barcode within padded area
    const xBarcode = Math.max(PADDING, Math.floor((labelW - barcodeWidth) / 2));

    // ZPL: interpretation line (built-in) vs our own label:
    // We'll use printer's built-in interpretation line when showLabel=true
    // and NOT draw our own text line, because it stays tied to the barcode.
    const interp = showLabel ? "Y" : "N";

    out.push("^XA");
    out.push(`^PW${labelW}`);
    out.push(`^LL${labelH}`);
    out.push("^CI28");

    // TOP
    const topSpec = `,${yTop}${fontSpec(FONT_SIZE)}^FD`;
    if (hasTop) {
      if (top[0]) out.push(`^FO${xLeft}${topSpec}${top[0]}^FS`);
      if (top[1]) out.push(`^FO${xCenterTop}${topSpec}${top[1]}^FS`);
      if (top[2]) out.push(`^FO${xRightTop}${topSpec}${top[2]}^FS`);
    }

    // BARCODE
    out.push(`^FO${xBarcode},${yBarcodeTop}`);
    out.push(`^BY${MODULE_WIDTH},${WIDE_NARROW_RATIO},${barsH}`);

    // Different barcode commands have slightly different parameter lists.
    // We’ll emit common patterns:
    // - Code39 (^B3): ^B3o,h,f,g,e
    // - Code128 (^BC): ^BCo,h,f,g,e
    // - EAN/UPC: they differ; but most accept: o,h,f,g
    // To keep it simple and consistent, we handle the main ones explicitly.
    if (bcCmd === "^B3") {
      out.push(`^B3N,${barsH},${interp},N,N`);
    } else if (bcCmd === "^BC") {
      out.push(`^BCN,${barsH},${interp},N,N`);
    } else if (bcCmd === "^BE") {
      out.push(`^BEN,${barsH},${interp},N`);
    } else if (bcCmd === "^B8") {
      out.push(`^B8N,${barsH},${interp},N`);
    } else if (bcCmd === "^BU") {
      out.push(`^BUN,${barsH},${interp},N`);
    } else if (bcCmd === "^B2") {
      out.push(`^B2N,${barsH},${interp},N,N`);
    } else if (bcCmd === "^BK") {
      out.push(`^BKN,${barsH},${interp},N,N`);
    } else {
      out.push(`^BCN,${barsH},${interp},N,N`);
    }

    out.push(`^FD${code}^FS`);

    // BOTTOM
    const botSpec = `,${yBottom}${fontSpec(FONT_SIZE)}^FD`;
    if (hasBottom) {
      if (bottom[0]) out.push(`^FO${xLeft}${botSpec}${bottom[0]}^FS`);
      if (bottom[1]) out.push(`^FO${xCenterBottom}${botSpec}${bottom[1]}^FS`);
      if (bottom[2]) out.push(`^FO${xRightBottom}${botSpec}${bottom[2]}^FS`);
    }

    out.push("^XZ");
  }

  return out.join("\n");
}

export const generateZplString = ({
  format = "CODE39",
  showLabel = true,
  size = {},
  barcodes = [],
  useBmp = true,
}) => {
  const generateFn = useBmp ? generateBmpZpl : generateZpl;
  const zpl = generateFn({ format, showLabel, size, barcodes });
  return zpl;
};

export function downloadZplFile({
  format = "CODE39",
  showLabel = true,
  size = {},
  barcodes = [],
  filename = "labels.zpl",
  useBmp = true,
  snackbar = {},
  returnString,
} = {}) {
  const {
    addSnackbarSuccess = () => {},
    addSnackbarInfo = () => {},
    addSnackbarError = () => {},
  } = snackbar;

  try {
    if (!Array.isArray(barcodes) || barcodes.length === 0) return;

    !returnString && addSnackbarInfo({ message: "Starting Download" });

    const generateFn = useBmp ? generateBmpZpl : generateZpl;
    const zpl = generateFn({ format, showLabel, size, barcodes });

    if (returnString) return zpl;

    // Create a blob and download it
    const blob = new Blob([zpl], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename.endsWith(".zpl") ? filename : `${filename}.zpl`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Cleanup
    URL.revokeObjectURL(url);

    addSnackbarSuccess({ message: "Download Successful" });

    return zpl;
  } catch (e) {
    console.log("Failed to download file", e);
    addSnackbarError({ message: "Failed to Download File" });
  }
}

export const useAvailablePrinters = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const fetch = () => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    BrowserPrint.getLocalDevices(
      (devices) => {
        console.log(devices);
        setData(devices ?? []);
        setIsLoading(false);
      },
      (err) => {
        console.error("getLocalDevices error:", err);
        setError(err);
        setIsError(true);
        setIsLoading(false);
      },
      "printer",
    );
  };

  useEffect(() => {
    fetch();
  }, []);

  return { data, isLoading, isError, error, refetch: fetch };
};

// used to print from desktop
export const printZpl = ({ file, device, snackbar = {} }) => {
  const { addSnackbarError = () => {} } = snackbar;

  const throwError = (error) => {
    addSnackbarError({ message: "Failed to Print ZPL" });
    console.log("Print error:", error);
  };

  try {
    const sendPrint = (printer) => printer.send(file, "barcodes", throwError);
    if (device) sendPrint(device);
    else BrowserPrint?.getDefaultDevice("printer", sendPrint);
  } catch (e) {
    throwError(e);
  }
};
